import { Component, ViewChild } from '@angular/core';
import { UsersService } from 'src/app/services/firestore';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { RoleModel, UserModel } from 'src/app/models';
import { ActionProgressComponent } from 'src/app/modules/shared/action-progress/action-progress.component';
import { UserNotificationSettingsModel } from 'src/app/models/user';
import { SaveSteps, failureMessage } from 'src/app/utils/resilience/save-steps';

interface CreateUserRequest {
  first_name?: string;
  last_name?: string;
  email: string;
  password: string;
}

interface CreateUserResponse {
  uid: string;
}

const ACCOUNT_STEP = 'account';
const PROFILE_STEP = 'profile';

@Component({
  selector: 'app-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
})
export class AddUserModalComponent {
  user: UserModel;
  loading: boolean;
  password: string;
  roles: RoleModel[];

  private saveSteps: SaveSteps | null = null;

  @ViewChild(ActionProgressComponent) actionProgress: ActionProgressComponent;
  constructor(private usersService: UsersService, public modal: BsModalRef, private fns: AngularFireFunctions, private toaster: ToastrService) {
    this.user = new UserModel();
    this.user.notificationSettings = new UserNotificationSettingsModel();
    this.loading = false;
  }

  /** True once the login account exists; a later save then only writes the profile. */
  get accountCreated(): boolean {
    return this.saveSteps?.isCompleted(ACCOUNT_STEP) ?? false;
  }

  async save() {
    if (!this.accountCreated && !this.isPasswordValid()) {
      return;
    }

    this.actionProgress.start();
    this.loading = true;
    const steps = (this.saveSteps ??= this.buildSaveSteps());

    try {
      await steps.run();
      this.saveSteps = null;
      this.modal.hide();
      this.toaster.success('User created');
    } catch (error) {
      this.toaster.error(failureMessage(error));
    } finally {
      this.loading = false;
      this.actionProgress.complete();
    }
  }

  private isPasswordValid(): boolean {
    if (this.password.length < 15) {
      this.toaster.error('The password must have at least 15 characters');
      return false;
    }

    if (!/[A-Z]/.test(this.password) || !/[a-z]/.test(this.password) || !/\d/.test(this.password)) {
      this.toaster.error('The password must containt at least one lower case (a-z), one upper case (A-Z) and one number');
      return false;
    }

    return true;
  }

  /**
   * Creating a user writes to two places: the login account (cloud function, which also
   * creates the Auth0 user) and the dashboard profile in Firestore. If the profile write
   * fails, the account already exists, so the steps are resumable and only the profile is
   * written again.
   */
  private buildSaveSteps(): SaveSteps {
    const createUser = this.fns.httpsCallable<CreateUserRequest, CreateUserResponse>('api/createUser');
    // The email the account was created with. The form stays editable while the modal is
    // open, so the profile is written with this value rather than a later edit.
    let accountEmail: string | null = null;

    return new SaveSteps([
      {
        key: ACCOUNT_STEP,
        label: 'creating the login account',
        // The function reuses an orphaned account (login exists, no dashboard profile), so a
        // retry after a timeout or a transient failure cannot create a duplicate. A duplicate
        // with an existing profile comes back as invalid-argument, which is never retried.
        run: async () => {
          const userRecord = await firstValueFrom(
            createUser({
              first_name: this.user.firstName,
              last_name: this.user.lastName,
              email: this.user.email,
              password: this.password,
            }),
          );
          this.user.id = userRecord.uid;
          accountEmail = this.user.email;
        },
      },
      {
        key: PROFILE_STEP,
        label: 'saving the dashboard profile',
        run: () => {
          if (accountEmail !== null) {
            this.user.email = accountEmail;
          }
          this.user.notificationSettings.email = true;
          this.user.notificationSettings.in_app = true;
          this.user.notificationSettings.count = 0;
          return this.usersService.addUser(this.user);
        },
      },
    ]);
  }
}
